import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  InputAdornment,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
  Alert,
} from "@mui/material";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";

import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import { Link as RouterLink } from "react-router-dom";
import { ROUTES } from "../../../shared/utils";
import { AuthCardTopBar, AuthCardIntro } from "./AuthCardHeader";
import { useCreateUser } from "../mutations";

type SignupStep = "details" | "success";

interface SignupFlowProps {
  onBackToLogin: () => void;
}

const COMPANY_OPTIONS = [
  { label: "PEAK4 ", value: "peak4-health" },
  { label: "Sunbelt", value: "sunbelt" },
] as const;

export const SignupFlow: React.FC<SignupFlowProps> = ({ onBackToLogin }) => {
  const [step, setStep] = useState<SignupStep>("details");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [company, setCompany] = useState<string>("");
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>(
    {}
  );
  const [error, setError] = useState("");

  const createUserMutation = useCreateUser();

  const emailRegex = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/, []);

  const isFirstNameValid = firstName.trim().length > 0;
  const isLastNameValid = lastName.trim().length > 0;
  const isEmailValid = emailRegex.test(email);
  const isCompanyValid = company.length > 0;

  const showFirstNameError = touchedFields.firstName && !isFirstNameValid;
  const showLastNameError = touchedFields.lastName && !isLastNameValid;
  const showEmailError = touchedFields.email && !isEmailValid;
  const showCompanyError = touchedFields.company && !isCompanyValid;

  const handleDetailsSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();
    setTouchedFields({
      firstName: true,
      lastName: true,
      email: true,
      company: true,
    });
    setError("");

    if (isFirstNameValid && isLastNameValid && isEmailValid && isCompanyValid) {
      try {
        await createUserMutation.mutateAsync({
          email: email.trim(),
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          company: company,
          requires_consent: true,
          phone_number: phoneNumber.trim() || undefined,
        });
        setStep("success");
      } catch (error) {
        setError(
          error instanceof Error
            ? error.message
            : "Failed to create account. Please try again."
        );
      }
    }
  };

  if (step === "success") {
    return (
      <Card
        variant="outlined"
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          borderColor: "divider",
          borderRadius: 4,
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            p: 4,
          }}
        >
          <Stack spacing={4}>
            <AuthCardTopBar />
            <Stack spacing={2} alignItems="center" textAlign="center">
              <CheckCircleOutlineRoundedIcon
                color="success"
                sx={{ fontSize: 64 }}
              />
              <Typography variant="h5" component="h1" fontWeight={600}>
                Account created successfully!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                We&apos;ve sent a temporary password to your email. Use it to
                sign in and set up your new password.
              </Typography>
            </Stack>
            <Button
              size="large"
              variant="contained"
              fullWidth
              endIcon={<ArrowForwardRoundedIcon />}
              onClick={onBackToLogin}
              sx={{ mt: 2 }}
            >
              Back to Sign In
            </Button>
          </Stack>
        </Box>
      </Card>
    );
  }

  return (
    <Card
      component="form"
      onSubmit={handleDetailsSubmit}
      variant="outlined"
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        borderColor: "divider",
        borderRadius: 4,
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          p: 4,
        }}
      >
        <Stack spacing={4}>
          <AuthCardTopBar />
          <AuthCardIntro
            title="Create your PEAK4 account"
            subtitle="Tell us about yourself so we can get your workspace ready."
          />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Stack spacing={3}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                fullWidth
                label="First name"
                value={firstName}
                onChange={(event) => {
                  setFirstName(event.target.value);
                  setTouchedFields((prev) => ({ ...prev, firstName: true }));
                }}
                onBlur={() =>
                  setTouchedFields((prev) => ({ ...prev, firstName: true }))
                }
                error={showFirstNameError}
                helperText={showFirstNameError ? "First name is required." : ""}
                required
              />
              <TextField
                fullWidth
                label="Last name"
                value={lastName}
                onChange={(event) => {
                  setLastName(event.target.value);
                  setTouchedFields((prev) => ({ ...prev, lastName: true }));
                }}
                onBlur={() =>
                  setTouchedFields((prev) => ({ ...prev, lastName: true }))
                }
                error={showLastNameError}
                helperText={showLastNameError ? "Last name is required." : ""}
                required
              />
            </Stack>
            <TextField
              fullWidth
              label="Email address"
              value={email}
              type="email"
              onChange={(event) => {
                setEmail(event.target.value);
                setTouchedFields((prev) => ({ ...prev, email: true }));
              }}
              onBlur={() =>
                setTouchedFields((prev) => ({ ...prev, email: true }))
              }
              error={showEmailError}
              placeholder="Enter your email address"
              helperText={
                showEmailError ? "Please enter a valid email address." : ""
              }
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MailOutlineRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Phone number"
              value={phoneNumber}
              onChange={(event) => setPhoneNumber(event.target.value)}
              placeholder="Enter your contact number"
            />
            <TextField
              select
              fullWidth
              label="Company"
              value={company}
              onChange={(event) => {
                setCompany(event.target.value);
                setTouchedFields((prev) => ({ ...prev, company: true }));
              }}
              onBlur={() =>
                setTouchedFields((prev) => ({ ...prev, company: true }))
              }
              error={showCompanyError}
              helperText={
                showCompanyError ? "Select the company you’re joining." : ""
              }
              required
            >
              {COMPANY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <Stack spacing={2}>
            <Button
              type="submit"
              size="large"
              variant="contained"
              fullWidth
              endIcon={<ArrowForwardRoundedIcon />}
              disabled={
                !firstName.length ||
                !lastName.length ||
                !email.length ||
                !company.length ||
                createUserMutation.isPending
              }
              sx={{ mt: 2 }}
            >
              {createUserMutation.isPending
                ? "Creating account..."
                : "Create Account"}
            </Button>
            <Link
              component={RouterLink}
              to={ROUTES.LOGIN}
              variant="body2"
              underline="hover"
              sx={{ alignSelf: "center", fontWeight: 500 }}
            >
              Already have an account? Sign in
            </Link>
          </Stack>
        </Stack>
      </Box>
    </Card>
  );
};
